import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/i18n/LangContext";
import { IMG } from "@/data/site";
import { Kicker, Reveal } from "./Reveal";

export const Equipment = () => {
  const { t } = useLang();
  return (
    <section id="attrezzatura" data-testid="equipment-section" className="relative bg-ink text-bone overflow-hidden">
      <div className="grain-layer" />
      <span
        aria-hidden="true"
        className="pointer-events-none select-none absolute left-0 right-0 bottom-[-4vw] text-center font-display text-[26vw] leading-none text-outline opacity-[0.06]"
      >
        IRON
      </span>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 grid lg:grid-cols-12 gap-14">
        {/* badge */}
        <div className="lg:col-span-5 order-2 lg:order-1">
          <Reveal className="relative max-w-md mx-auto">
            <div className="absolute -inset-10 bg-[radial-gradient(closest-side,rgba(215,25,42,0.35),transparent)] blur-2xl" />
            <div className="relative border-4 border-bone/90 bg-coal p-3 shadow-[14px_14px_0_#D7192A] -rotate-2 hover:rotate-0 transition-transform duration-500">
              <div className="relative overflow-hidden aspect-square">
                <img
                  src={IMG.badge}
                  alt="Targa Official Hammer Strength Training Center"
                  loading="lazy"
                  className="w-full h-full object-cover [filter:contrast(1.1)_saturate(0.7)_sepia(0.15)]"
                />
                <div className="grain-layer" />
                <div className="vignette" />
              </div>
              {["top-2 left-2", "top-2 right-2", "bottom-2 left-2", "bottom-2 right-2"].map((p) => (
                <span key={p} className={`absolute ${p} w-3 h-3 rounded-full bg-steel border border-ink`} />
              ))}
            </div>
            <p className="font-hand text-2xl text-hammer-red mt-8 rotate-[-3deg] text-center" data-testid="equipment-caption">
              ↑ {t.equipment.caption}
            </p>
          </Reveal>
        </div>

        {/* copy */}
        <div className="lg:col-span-7 order-1 lg:order-2">
          <Reveal>
            <Kicker light testid="equipment-kicker">{t.equipment.kicker}</Kicker>
            <h2 data-testid="equipment-title" className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[0.9]">
              Official <span className="text-hammer-red">Hammer Strength</span>
              <br />
              Training Center
            </h2>
            <p className="mt-6 text-bone/75 max-w-[56ch] leading-relaxed">{t.equipment.intro}</p>
          </Reveal>

          <ol className="mt-12 border-t-2 border-bone/20">
            {t.equipment.items.map((it, i) => (
              <Reveal as="li" key={i} delay={i * 0.06}>
                <div
                  data-testid={`equipment-item-${i}`}
                  className="grid grid-cols-[3.5rem_1fr] sm:grid-cols-[5rem_1fr] items-baseline gap-4 py-6 border-b-2 border-bone/20"
                >
                  <span className="font-mono text-sm text-hammer-red">/0{i + 1}</span>
                  <div>
                    <h3 className="font-display text-3xl sm:text-4xl leading-none">
                      {it.t}
                    </h3>
                    <p className="text-sm text-bone/65 mt-2 max-w-[52ch]">{it.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

export const Interlude = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const x1 = useTransform(scrollYProgress, [0, 1], ["-6%", "4%"]);
  const x2 = useTransform(scrollYProgress, [0, 1], ["6%", "-4%"]);
  return (
    <section ref={ref} data-testid="interlude-section" className="relative h-[85vh] min-h-[560px] overflow-hidden bg-ink text-bone torn-top torn-bottom -my-[14px] z-10">
      <motion.div style={{ y }} className="absolute inset-[-14%_0]">
        <img src={IMG.muralHd} alt="Macchine Hammer Strength e murale di Arnold" loading="lazy" decoding="async" className="w-full h-full object-cover photo-hero" />
      </motion.div>
      <div className="tint-red" />
      <div className="absolute inset-0 bg-ink/45 z-[1]" />
      <div className="relative z-10 h-full flex flex-col justify-center overflow-hidden">
        <motion.p style={{ x: x1 }} className="font-display text-[16vw] sm:text-[15vw] leading-[0.82] whitespace-nowrap pl-[4vw]" data-testid="interlude-line-1">
          {t.interlude.l1}
        </motion.p>
        <motion.p style={{ x: x2 }} className="font-display text-[16vw] sm:text-[15vw] leading-[0.82] whitespace-nowrap text-right pr-[6vw] text-outline-red" data-testid="interlude-line-2">
          {t.interlude.l2}
        </motion.p>
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 mt-8">
          <p className="max-w-md text-bone/85 leading-relaxed border-l-4 border-hammer-red pl-4 bg-ink/60 py-2">{t.interlude.sub}</p>
        </div>
      </div>
    </section>
  );
};
